<script setup lang="ts">
/**
 * Skip to main content link for keyboard users.
 * This component provides a way for keyboard users to skip over
 * repetitive navigation elements and jump directly to the main content.
 *
 * The link is visually hidden until it receives focus, making it
 * invisible to mouse users but accessible to keyboard users.
 *
 * @example
 * ```vue
 * <template>
 *   <SkipLink />
 *   <nav>...</nav>
 *   <main id="main-content">...</main>
 * </template>
 * ```
 */

interface Props {
  /**
   * The ID of the element to skip to (without the #).
   * @default 'main-content'
   */
  targetId?: string;
  /**
   * The text content of the skip link.
   * @default 'Skip to main content'
   */
  label?: string;
}

withDefaults(defineProps<Props>(), {
  targetId: 'main-content',
  label: 'Skip to main content',
});
</script>

<template>
  <a
    :href="`#${targetId}`"
    class="skip-link sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[9999] focus:rounded focus:bg-primary-600 focus:px-4 focus:py-2 focus:text-white focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-2"
  >
    {{ label }}
  </a>
</template>

<style scoped>
/*
 * Screen reader only styles (hidden visually but accessible to screen readers).
 * These are backup styles in case Tailwind's sr-only class is not available.
 */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}

/*
 * Override sr-only when focused to make the link visible.
 */
.skip-link:focus {
  position: absolute;
  width: auto;
  height: auto;
  padding: 0.5rem 1rem;
  margin: 0;
  overflow: visible;
  clip: auto;
  white-space: nowrap;
}
</style>
